import {
  ArrowUpRight,
  Bell,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Eye,
  EyeOff,
  Lightbulb,
  MessageSquare,
  Share2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  X,
} from "lucide-react";
import { Fragment, useState, useEffect, useRef } from "react";
import * as S from "../dashboard/_shared";
import "./style.scss";
import { firebaseAuth } from "../../config/firebase";
import NotificationService from "../../services/Notifications/notifications.service.jsx";
import { notificationStyles } from "../../constants/notificationsStyles.js";
import { toast } from "sonner";
import {
  OPEN_NOTIFICATIONS_EVENT,
  toastActionButtonStyle,
} from "../../hooks/useNotifications.js";
import {
  formatFullDateTime,
  formatRelativeTime,
  notificationDateGroup,
} from "../../utils/formatters.js";
import { fromApiItem } from "../../store/slices/actionPlansSlice.js";
import actionPlansService from "../../services/actionPlansService.js";
import {
  EMPTY_MESSAGES,
  MODAL_FILTERS,
  sortNotifications,
  filterNotifications,
  hasMoreToLoad,
} from "./dataTransforms.js";

// As cores das categorias são var(--...), então a transparência não pode ser
// um sufixo hex ("var(--color-red)08" é inválido e o navegador ignora).
const withAlpha = (color, pct) =>
  `color-mix(in srgb, ${color} ${pct}%, transparent)`;

export default function NotificationCenter({ ctx }) {
  const {
    notifications,
    setNotifications,
    notificationsLoading,
    setModal,
    setTab,
    setPrefilledAcao,
    notificationCounts,
    refreshNotificationCounts,
    loadMoreNotifications,
  } = ctx;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const containerRef = useRef(null);
  const [showHidden, setShowHidden] = useState(false);
  const [isCenterOpen, setIsCenterOpen] = useState(false);
  const [notifModalFiltro, setNotifModalFiltro] = useState("todas");
  const [userToken, setUserToken] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const setActionPlanModal = setModal;
  const setActionPlanTab = setTab;
  const sortedNotifications = sortNotifications(notifications);
  const visibleNotifications = sortedNotifications.filter((n) => !n.is_hidden);
  const hiddenNotifications = sortedNotifications.filter((n) => n.is_hidden);
  const unreadNotifications = notificationCounts.unread;

  const filtradas = filterNotifications(notifications, notifModalFiltro);
  // A lista tem só parte das notificações; o total do filtro vem da API.
  const filtroTotal = notificationCounts[MODAL_FILTERS[notifModalFiltro]];
  const hasMore = hasMoreToLoad(filtradas, notificationCounts, notifModalFiltro);

  const carregarMais = async () => {
    setIsLoadingMore(true);
    await loadMoreNotifications(
      MODAL_FILTERS[notifModalFiltro],
      filtradas.at(-1)?.id,
    );
    await refreshNotificationCounts();
    setIsLoadingMore(false);
  };

  const markNotificationAsRead = async (id) => {
    await NotificationService.markNotificationAsRead(id, userToken);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
    );
    refreshNotificationCounts();
  };

  // Abre o modal de plano de ação preenchido com o plano que a Eva sugeriu.
  // O suggested_plan vem no mesmo formato da API de planos (title, tasks...).
  const criarPlanoSugerido = (n) => {
    const { id, _apiId, ...plano } = fromApiItem({
      status: "em_andamento",
      ...n.action_payload.suggested_plan,
    });
    // Tarefas e links novos ainda não têm id; o modal usa o id para remover o item.
    const base = Date.now();
    setPrefilledAcao({
      ...plano,
      origem: "Eva",
      check: plano.check.map((c, i) => ({ ...c, i: base + i })),
      links: plano.links.map((l, i) => ({ ...l, id: base + i })),
    });
    setActionPlanModal("new");
    setIsPopoverOpen(false);
    if (!n.is_read) markNotificationAsRead(n.id);
  };

  // Abre o plano de ação da notificação de progresso (action_type "open_action_plan").
  const abrirPlano = async (n) => {
    if (!n.is_read) markNotificationAsRead(n.id);
    const res = await actionPlansService.getById(
      n.action_payload.action_plan_id,
    );
    if (!res?.data) {
      toast("Esse plano de ação não existe mais.");
      return;
    }
    setActionPlanModal(fromApiItem(res.data));
    setIsPopoverOpen(false);
    setIsCenterOpen(false);
  };

  const markAllNotificationsAsRead = async () => {
    await NotificationService.markAllNotificationsAsRead(userToken);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    refreshNotificationCounts();
  };

  // Clicar de novo na mesma reação desmarca (grava "none" também na API).
  const setNotificationReaction = async (id, reaction) => {
    const current = notifications.find((n) => n.id === id)?.reaction;
    const next = current === reaction ? "none" : reaction;
    await NotificationService.updateNotificationReaction(id, next, userToken);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, reaction: next } : n)),
    );
    refreshNotificationCounts();
  };

  const updateNotificationVisibility = async (id, isHidden) => {
    await NotificationService.updateNotificationVisibility(
      id,
      isHidden,
      userToken,
    );
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        return { ...n, is_hidden: n.is_hidden === isHidden ? null : isHidden };
      }),
    );
    refreshNotificationCounts();
    if (isHidden) {
      toast("Notificação ocultada", {
        action: {
          label: "Desfazer",
          onClick: () => updateNotificationVisibility(id, false),
        },
        actionButtonStyle: toastActionButtonStyle,
        duration: 5000,
      });
    }
  };

  // Ao abrir o modal ou trocar de filtro, busca a primeira página do filtro
  // se a lista carregada não tiver o bastante para preencher a tela.
  useEffect(() => {
    if (isCenterOpen && filtradas.length < Math.min(20, filtroTotal)) {
      carregarMais();
    }
  }, [isCenterOpen, notifModalFiltro]);

  // Fecha o popover ao clicar fora dele (sino incluso no "dentro") ou ao apertar Esc.
  useEffect(() => {
    if (!isPopoverOpen) return;
    const onPointerDown = (e) => {
      // Clicar no toast (ex.: "Desfazer" ao ocultar) não fecha o popover.
      if (e.target.closest?.("[data-sonner-toaster]")) return;
      if (!containerRef.current?.contains(e.target)) setIsPopoverOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsPopoverOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isPopoverOpen]);

  // Esc fecha o modal "Ver todas".
  useEffect(() => {
    if (!isCenterOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsCenterOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isCenterOpen]);

  // Botão "Ver" do toast de notificações abre a central.
  useEffect(() => {
    const open = () => setIsPopoverOpen(true);
    window.addEventListener(OPEN_NOTIFICATIONS_EVENT, open);
    return () => window.removeEventListener(OPEN_NOTIFICATIONS_EVENT, open);
  }, []);

  useEffect(() => {
    const fetchUserToken = async () => {
      const userToken = await firebaseAuth.currentUser.getIdToken();
      setUserToken(userToken);
    };
    fetchUserToken();
  }, []);

  return (
    <>
      <div className="compNotification" ref={containerRef}>
        <button
          onClick={() => setIsPopoverOpen((o) => !o)}
          title="Notificações"
          className={`compNotification__btn${isPopoverOpen ? " is-open" : ""}`}
        >
          <Bell
            size={15}
            color={isPopoverOpen ? "var(--color-g700)" : "var(--color-text-2)"}
          />
          {unreadNotifications > 0 && (
            <div className="compNotification__badge">
              {unreadNotifications > 99 ? "99+" : unreadNotifications}
            </div>
          )}
        </button>
        {isPopoverOpen && (
          <>
            <div className="compNotification__popover">
              {/* Header do popover */}
              <div
                className="compNotification__popover-header"
                style={{
                  background: `linear-gradient(135deg, var(--color-green-bg, var(--color-surface-2)) 0%, var(--color-surface) 100%)`,
                }}
              >
                <div className="compNotification__popover-header-left">
                  <div className="compNotification__popover-icon">
                    <Bell size={14} color="#FFFFFF" />
                  </div>
                  <div>
                    <div className="compNotification__popover-title">
                      Notificações
                    </div>
                    <div className="compNotification__popover-subtitle">
                      {unreadNotifications > 0
                        ? `${unreadNotifications} não ${unreadNotifications === 1 ? "lida" : "lidas"}`
                        : "Tudo em dia"}
                    </div>
                  </div>
                </div>
                {unreadNotifications > 0 && (
                  <button
                    onClick={markAllNotificationsAsRead}
                    className="compNotification__mark-all-btn"
                  >
                    Marcar todas
                  </button>
                )}
              </div>
              {/* Lista de notificações */}
              <div className="compNotification__list">
                {visibleNotifications.length === 0 ? (
                  <div className="compNotification__empty">
                    {notificationsLoading
                      ? "Carregando notificações…"
                      : "Nenhuma mensagem ainda."}
                  </div>
                ) : (
                  visibleNotifications.slice(0, 6).map((n) => {
                    const cfg =
                      notificationStyles[n.category] ||
                      notificationStyles.announcement;
                    const Ico = cfg.icon;
                    const isEvaPlano =
                      n.action_type === "create_action_plan" &&
                      n.action_payload?.suggested_plan;
                    const isAbrirPlano =
                      n.action_type === "open_action_plan" &&
                      n.action_payload?.action_plan_id;
                    const isBriefing = n.category === "briefing";
                    const utilActive = n.reaction === "like";
                    const naoActive = n.reaction === "dislike";
                    return (
                      <div
                        key={n.id}
                        onClick={() => markNotificationAsRead(n.id)}
                        className="compNotification__item"
                        style={{
                          "--item-cor": cfg.color,
                          "--item-icon-bg": cfg.bg,
                          "--item-bg": n.is_read
                            ? "transparent"
                            : withAlpha(cfg.color, 3),
                          "--item-dot-color": cfg.color,
                          "--item-eva-shadow": `0 2px 8px ${withAlpha(cfg.color, 25)}`,
                          "--fb-util-bg": utilActive
                            ? "var(--color-green-bg)"
                            : "transparent",
                          "--fb-util-border": utilActive
                            ? "var(--color-g700)"
                            : "var(--color-border)",
                          "--fb-util-color": utilActive
                            ? "var(--color-g700)"
                            : "var(--color-text-4)",
                          "--fb-nao-bg": naoActive
                            ? "var(--color-red-bg)"
                            : "transparent",
                          "--fb-nao-border": naoActive
                            ? "var(--color-red)"
                            : "var(--color-border)",
                          "--fb-nao-color": naoActive
                            ? "var(--color-red)"
                            : "var(--color-text-4)",
                        }}
                      >
                        {!n.is_read && (
                          <div className="compNotification__unread-dot" />
                        )}
                        <div className="compNotification__item-icon">
                          <Ico size={14} color={cfg.color} />
                        </div>
                        <div className="compNotification__item-body">
                          <div className="compNotification__item-head">
                            <div
                              className={`compNotification__item-title compNotification__item-title--${n.is_read ? "read" : "unread"}`}
                            >
                              {n.title}
                            </div>
                            {isBriefing && n.tempoLeitura && (
                              <span className="compNotification__reading-time">
                                ⏱ {n.tempoLeitura}
                              </span>
                            )}
                            <div
                              className="compNotification__item-when"
                              title={formatFullDateTime(n.created_at)}
                            >
                              {formatRelativeTime(n.created_at)}
                            </div>
                          </div>
                          <div className="compNotification__item-body-text">
                            {n.message}
                          </div>
                          {/* Botão especial pro eva_plano */}
                          {isEvaPlano && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                criarPlanoSugerido(n);
                              }}
                              className="compNotification__eva-btn"
                            >
                              <Sparkles size={11} /> Criar plano de ação
                              automaticamente
                            </button>
                          )}
                          {isAbrirPlano && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirPlano(n);
                              }}
                              className="compNotification__eva-btn"
                            >
                              <ArrowUpRight size={11} /> Abrir plano de ação
                            </button>
                          )}
                          {/* Barra de feedback inline (útil / não gostei / ocultar) */}
                          <div className="compNotification__feedback">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotificationReaction(n.id, "like");
                              }}
                              title="Útil"
                              className="compNotification__feedback-btn compNotification__feedback-btn--util"
                            >
                              <ThumbsUp size={10} /> Útil
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotificationReaction(n.id, "dislike");
                              }}
                              title="Não gostei"
                              className="compNotification__feedback-btn compNotification__feedback-btn--nao"
                            >
                              <ThumbsDown size={10} /> Não gostei
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateNotificationVisibility(n.id, true);
                              }}
                              title="Ocultar esta notificação"
                              className="compNotification__feedback-btn compNotification__feedback-btn--hide"
                            >
                              <EyeOff size={10} /> Ocultar
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                {/* Seção de ocultos — colapsável, só aparece se houver itens ocultos */}
                {hiddenNotifications.length > 0 && (
                  <div className="compNotification__hidden-section">
                    <button
                      onClick={() => setShowHidden((m) => !m)}
                      className="compNotification__hidden-toggle"
                    >
                      <EyeOff size={12} />
                      <span className="compNotification__hidden-toggle-label">
                        {hiddenNotifications.length}{" "}
                        {hiddenNotifications.length === 1
                          ? "ocultada"
                          : "ocultadas"}
                      </span>
                      {showHidden ? (
                        <ChevronUp size={12} />
                      ) : (
                        <ChevronDown size={12} />
                      )}
                    </button>
                    {showHidden && (
                      <div className="compNotification__hidden-list">
                        {hiddenNotifications.map((n) => {
                          const cfgOc =
                            notificationStyles[n.category] ||
                            notificationStyles.announcement;
                          const IcoOc = cfgOc.icon;
                          return (
                            <div
                              key={n.id}
                              className="compNotification__hidden-item"
                            >
                              <div className="compNotification__hidden-icon">
                                <IcoOc size={12} color={cfgOc.color} />
                              </div>
                              <div className="compNotification__hidden-body">
                                <div className="compNotification__hidden-title">
                                  {n.title}
                                </div>
                                <div className="compNotification__hidden-text">
                                  {n.message}
                                </div>
                              </div>
                              <button
                                onClick={() =>
                                  updateNotificationVisibility(n.id, false)
                                }
                                title="Mostrar de novo"
                                className="compNotification__show-btn"
                              >
                                <Eye size={10} /> Mostrar
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Footer do popover */}
              <div className="compNotification__footer">
                <span className="compNotification__footer-count">
                  {notificationCounts.visible > 6
                    ? `Mostrando 6 de ${notificationCounts.visible}`
                    : `${notificationCounts.visible} notificaç${notificationCounts.visible === 1 ? "ão" : "ões"}`}
                </span>
                <button
                  onClick={() => {
                    setIsPopoverOpen(false);
                    setIsCenterOpen(true);
                  }}
                  className="compNotification__view-all-btn"
                >
                  Ver todas <ArrowUpRight size={11} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Modal fullscreen "Ver todas" notificações */}
      {isCenterOpen && (
        <div
          className="compNotification__modal-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && setIsCenterOpen(false)
          }
        >
          {/* Header */}
          <div
            className="compNotification__modal-header"
            style={{
              background: `linear-gradient(135deg, var(--color-green-bg, var(--color-surface-2)) 0%, var(--color-surface) 100%)`,
            }}
          >
            <div className="compNotification__modal-icon">
              <Bell size={22} color="#FFFFFF" />
            </div>
            <div className="compNotification__modal-meta">
              <div className="compNotification__modal-eyebrow">
                Central de notificações
              </div>
              <div className="compNotification__modal-title">
                Todas as notificações
              </div>
              <div className="compNotification__modal-count">
                {notificationCounts.visible} no total · {unreadNotifications}{" "}
                não lida
                {unreadNotifications === 1 ? "" : "s"}
              </div>
            </div>
            {unreadNotifications > 0 && (
              <button
                onClick={markAllNotificationsAsRead}
                className="compNotification__modal-mark-btn"
              >
                Marcar todas como lidas
              </button>
            )}
            <button
              onClick={() => setIsCenterOpen(false)}
              className="compNotification__modal-close-btn"
            >
              <X size={18} />
            </button>
          </div>
          {/* Filtros */}
          <div className="compNotification__filters">
            {[
              {
                k: "todas",
                lbl: "Todas",
                count: notificationCounts.visible,
              },
              {
                k: "nao_lidas",
                lbl: "Não lidas",
                count: unreadNotifications,
              },
              {
                k: "uteis",
                lbl: "👍 Marcadas como úteis",
                count: notificationCounts.liked,
              },
              {
                k: "ocultas",
                lbl: "Ocultadas",
                count: notificationCounts.hidden,
              },
            ].map((f) => {
              const active = notifModalFiltro === f.k;
              return (
                <button
                  key={f.k}
                  onClick={() => setNotifModalFiltro(f.k)}
                  className={`compNotification__filter-btn compNotification__filter-btn--${active ? "active" : "inactive"}`}
                >
                  {f.lbl}
                  <span
                    className={`compNotification__filter-count compNotification__filter-count--${active ? "active" : "inactive"}`}
                  >
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Lista de notificações em layout largo */}
          <div className="compNotification__modal-list">
            <div className="compNotification__modal-list-inner">
              {filtradas.length === 0 && !hasMore && (
                <div className="compNotification__modal-empty">
                  {EMPTY_MESSAGES[notifModalFiltro]}
                </div>
              )}
              {filtradas.map((n, idx) => {
                // Título do grupo (Hoje, Ontem...) antes da primeira notificação de cada grupo.
                const group = notificationDateGroup(n.created_at);
                const isFirstOfGroup =
                  idx === 0 ||
                  notificationDateGroup(filtradas[idx - 1].created_at) !==
                    group;
                const cfg =
                  notificationStyles[n.category] ||
                  notificationStyles.announcement;
                const Ico = cfg.icon;
                const isEvaPlano =
                  n.action_type === "create_action_plan" &&
                  n.action_payload?.suggested_plan;
                const isAbrirPlano =
                  n.action_type === "open_action_plan" &&
                  n.action_payload?.action_plan_id;
                const utilActive = n.reaction === "like";
                const naoActive = n.reaction === "dislike";
                return (
                  <Fragment key={n.id}>
                    {isFirstOfGroup && (
                      <div className="compNotification__modal-group">
                        {group}
                      </div>
                    )}
                    <div
                      onClick={() => markNotificationAsRead(n.id)}
                      className="compNotification__modal-item"
                      style={{
                        "--item-cor": cfg.color,
                        "--item-icon-bg": cfg.bg,
                        "--item-eva-shadow": `0 2px 8px ${withAlpha(cfg.color, 25)}`,
                        "--modal-item-border": n.is_read
                          ? "var(--color-border)"
                          : withAlpha(cfg.color, 25),
                        "--modal-item-shadow": n.is_read
                          ? "none"
                          : `0 2px 12px ${withAlpha(cfg.color, 8)}`,
                        "--fb-util-bg": utilActive
                          ? "var(--color-green-bg)"
                          : "transparent",
                        "--fb-util-border": utilActive
                          ? "var(--color-g700)"
                          : "var(--color-border)",
                        "--fb-util-color": utilActive
                          ? "var(--color-g700)"
                          : "var(--color-text-4)",
                        "--fb-nao-bg": naoActive
                          ? "var(--color-red-bg)"
                          : "transparent",
                        "--fb-nao-border": naoActive
                          ? "var(--color-red)"
                          : "var(--color-border)",
                        "--fb-nao-color": naoActive
                          ? "var(--color-red)"
                          : "var(--color-text-4)",
                      }}
                    >
                      <div className="compNotification__modal-item-icon">
                        <Ico size={20} color={cfg.color} />
                      </div>
                      <div className="compNotification__modal-item-body">
                        <div className="compNotification__modal-item-head">
                          <div
                            className={`compNotification__modal-item-title compNotification__modal-item-title--${n.is_read ? "read" : "unread"}`}
                          >
                            {n.title}
                          </div>
                          {!n.is_read && (
                            <span className="compNotification__new-badge">
                              Nova
                            </span>
                          )}
                          <div
                            className="compNotification__modal-item-when"
                            title={formatFullDateTime(n.created_at)}
                          >
                            {formatRelativeTime(n.created_at)}
                          </div>
                        </div>
                        <div className="compNotification__modal-item-text">
                          {n.message}
                        </div>
                        {isEvaPlano && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              criarPlanoSugerido(n);
                              setIsCenterOpen(false);
                            }}
                            className="compNotification__eva-btn-modal"
                          >
                            <Sparkles size={12} /> Criar plano de ação
                            automaticamente
                          </button>
                        )}
                        {isAbrirPlano && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirPlano(n);
                            }}
                            className="compNotification__eva-btn-modal"
                          >
                            <ArrowUpRight size={12} /> Abrir plano de ação
                          </button>
                        )}
                        {/* Barra de feedback inline (modal) */}
                        <div className="compNotification__modal-feedback">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setNotificationReaction(n.id, "like");
                            }}
                            className="compNotification__modal-feedback-btn compNotification__modal-feedback-btn--util"
                          >
                            <ThumbsUp size={12} /> Útil
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setNotificationReaction(n.id, "dislike");
                            }}
                            className="compNotification__modal-feedback-btn compNotification__modal-feedback-btn--nao"
                          >
                            <ThumbsDown size={12} /> Não gostei
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateNotificationVisibility(n.id, !n.is_hidden);
                            }}
                            className="compNotification__modal-feedback-btn compNotification__modal-feedback-btn--hide"
                          >
                            {n.is_hidden ? (
                              <>
                                <Eye size={12} /> Mostrar
                              </>
                            ) : (
                              <>
                                <EyeOff size={12} /> Ocultar
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Fragment>
                );
              })}
              {hasMore && (
                <button
                  onClick={carregarMais}
                  disabled={isLoadingMore}
                  className="compNotification__load-more-btn"
                >
                  {isLoadingMore
                    ? "Carregando…"
                    : `Carregar mais (${filtroTotal - filtradas.length})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
